import { FC } from 'react';
import { Stack } from 'src/components/layout/stack';
import { Tooltip } from 'src/components/overlay/tooltip';
import { labelsToOptions } from 'src/utils/labels-to-options';
import styled from 'styled-components';

export type FlowDirection = 'ENTRY' | 'EXIT';
export type DirectionType = FlowDirection | 'BUNDLE';

export const flowDirectionLabels: Record<FlowDirection, string> = {
  ENTRY: 'Entry',
  EXIT: 'Exit',
};

export const directionLabels: Record<DirectionType, string> = {
  ...flowDirectionLabels,
  BUNDLE: 'Exit/Entry',
};

export const flowDirectionOptions = labelsToOptions(flowDirectionLabels);
export const directionOptions = labelsToOptions(directionLabels);

function getDirectionLabel(direction: DirectionType): string {
  return {
    ENTRY: 'Entry',
    EXIT: 'Exit',
    BUNDLE: 'Bundle',
  }[direction];
}

export type NetworkPoint =
  | { direction: DirectionType }
  | { direction: FlowDirection; marketArea0: string }
  | { direction: FlowDirection; marketer: string }
  | { direction: FlowDirection; marketAreaName: string }
  | { direction: 'BUNDLE'; marketArea0: string; marketArea1: string }
  | { exit: { marketArea: string }; entry: { marketArea: string } }
  | { exit: { marketer: string }; entry: { marketer: string } }
  | { exit: { marketArea: string } }
  | { exit: { marketer: string } }
  | { entry: { marketArea: string } }
  | { entry: { marketer: string } };

type DirectionBaseProps =
  | ({ direction: DirectionType } & {
      entryMarketArea?: never;
      exitMarketArea?: never;
    })
  | { direction: 'ENTRY'; entryMarketArea: string; exitMarketArea?: never }
  | { direction: 'EXIT'; exitMarketArea: string; entryMarketArea?: never }
  | { direction: 'BUNDLE'; entryMarketArea: string; exitMarketArea: string };

function getDirection(nwp: NetworkPoint): DirectionType {
  if ('direction' in nwp) return nwp.direction;
  if ('exit' in nwp && 'entry' in nwp) return 'BUNDLE';
  if ('exit' in nwp) return 'EXIT';
  if ('entry' in nwp) return 'ENTRY';
  throw `Found unsupported network point type: ${nwp}`;
}

function getExitMarketArea(nwp: NetworkPoint, direction: DirectionType) {
  if ('marketArea0' in nwp && direction !== 'ENTRY') return nwp.marketArea0;
  if ('marketAreaName' in nwp && direction === 'EXIT')
    return nwp.marketAreaName;
  if ('marketer' in nwp && direction === 'EXIT') return nwp.marketer;
  if ('exit' in nwp && 'marketArea' in nwp.exit) return nwp.exit.marketArea;
  if ('exit' in nwp && 'marketer' in nwp.exit) return nwp.exit.marketer;
  return undefined;
}

function getEntryMarketArea(nwp: NetworkPoint, direction: DirectionType) {
  if ('marketArea0' in nwp && direction === 'ENTRY') return nwp.marketArea0;
  if ('marketArea1' in nwp && direction === 'BUNDLE') return nwp.marketArea1;
  if ('marketAreaName' in nwp && direction === 'ENTRY')
    return nwp.marketAreaName;
  if ('marketer' in nwp && direction === 'ENTRY') return nwp.marketer;
  if ('entry' in nwp && 'marketArea' in nwp.entry) return nwp.entry.marketArea;
  if ('entry' in nwp && 'marketer' in nwp.entry) return nwp.entry.marketer;
  return undefined;
}

export function convertDirectionProps(nwp: NetworkPoint) {
  const direction = getDirection(nwp);
  const exitMarketArea = getExitMarketArea(nwp, direction);
  const entryMarketArea = getEntryMarketArea(nwp, direction);
  return { direction, exitMarketArea, entryMarketArea } as DirectionBaseProps;
}

export type DirectionProps = {
  networkPoint: NetworkPoint;
};

const TooltipContent: FC<DirectionBaseProps> = (props) => {
  const { direction, entryMarketArea, exitMarketArea } = props;
  switch (direction) {
    case 'BUNDLE':
      return (
        <small data-testid="direction-tooltip">
          <Stack
            flow="column"
            templateColumns="1fr 1fr"
            gap={1}
            alignItems="start"
          >
            {exitMarketArea && (
              <span>
                <strong>{directionLabels['EXIT']}</strong>
                <br />
                {exitMarketArea}
              </span>
            )}
            {entryMarketArea && (
              <span>
                <strong>{directionLabels['ENTRY']}</strong>
                <br />
                {entryMarketArea}
              </span>
            )}
          </Stack>
        </small>
      );
    default:
      return (
        <small data-testid="direction-tooltip">
          <Stack>
            <strong>{directionLabels[direction]}</strong>
            {entryMarketArea ? entryMarketArea : exitMarketArea}
          </Stack>
        </small>
      );
  }
};

export const Direction: FC<DirectionProps> = ({ networkPoint }) => {
  const direction = convertDirectionProps(networkPoint);

  if (direction.entryMarketArea || direction.exitMarketArea) {
    return (
      <Tooltip content={<TooltipContent {...direction} />}>
        {(targetProps) => (
          <Text
            data-testid={direction.direction}
            direction={direction.direction}
            {...targetProps}
          >
            {getDirectionLabel(direction.direction)}
          </Text>
        )}
      </Tooltip>
    );
  } else {
    return (
      <Text data-testid={direction.direction} direction={direction.direction}>
        {getDirectionLabel(direction.direction)}
      </Text>
    );
  }
};

const colors = {
  BUNDLE: '#7879B3',
  EXIT: '#CA85AC',
  ENTRY: '#56A7D8',
} as const;

const Text = styled.small<{ direction: DirectionType }>`
  font-weight: bold;
  color: ${({ direction }) => colors[direction]};
`;
